import React, { useEffect, useState } from "react";

import Editor from "react-simple-code-editor";

import { Button } from "@/design-system/components/Button/Button";
import { Dialog } from "@/design-system/components/Dialog/Dialog";
import { toast } from "@/design-system/components/Toast/Toast";

import { highlight, languages } from "@/lib/prism";

import useCurrentEnvironment from "@/hooks/use-environment";
import useAccountMetadata from "@/hooks/useAccountMetadata";
import { makeRequest } from "@/util";
import { TransformOperationOptionType } from "../types";

export const SQLEditor: React.FC<{
  initialQuery: string | null;
  queryType: TransformOperationOptionType | null;
  onClose: () => void;
  onSave: (query: string) => void;
}> = ({ initialQuery, queryType, onClose, onSave }) => {
  const currentEnvironment = useCurrentEnvironment();
  const accountMetadata = useAccountMetadata();
  const [editorQuery, setEditorQuery] = useState(initialQuery || "");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const sqlTransformationsAvailable =
    !!currentEnvironment?.project?.config["sqlTransformations"] ||
    !!accountMetadata.config["sqlTransformationsv2"];

  useEffect(() => {
    if (!sqlTransformationsAvailable) {
      setErrorMessage(
        `Contact support@freshpaint.io to enable this feature for the ${currentEnvironment?.project?.name} project. Provide your project ID: ${currentEnvironment?.project?.ID}.`,
      );
    }
  }, [currentEnvironment, sqlTransformationsAvailable]);

  function handleSave() {
    makeRequest<{ error_message?: string }>("test_sql_transformation", {
      query: editorQuery,
      // @ts-ignore
      queryType: queryType?.value,
    })
      .then((resp) => {
        if (resp.data.error_message) {
          setErrorMessage(resp.data.error_message);
        } else {
          onSave(editorQuery);
        }
      })
      .catch((error) => {
        toast.error("Something went wrong. Unable to save transformation.");
        console.error(error);
      });
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Edit custom SQL transformation"
      description={
        <>
          Your SQL runs against each incoming event, which you query as the
          table <code>incoming_event</code>. The fields your query returns are
          added to the event as properties. For the table’s schema and example
          queries, see the{" "}
          <a
            className="text-textActionSelected cursor-pointer"
            href="https://documentation.freshpaint.io/your-freshpaint-account/events/transformations/sql"
          >
            docs on custom SQL transformations
          </a>
          .
        </>
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => handleSave()}
            disabled={!sqlTransformationsAvailable}
          >
            Save
          </Button>
        </>
      }
    >
      <Editor
        className="bg-surface-muted text-textStrong min-h-30 border-2 px-2 py-3 font-mono text-xs"
        padding={12}
        onValueChange={setEditorQuery}
        value={editorQuery}
        placeholder="SELECT 'example_value' AS example_property FROM incoming_event;"
        highlight={(code) => highlight(code, languages.sql, "sql")}
      />
      <p className="text-textError mt-2 text-xs">{errorMessage}</p>
    </Dialog>
  );
};
